/**
 * Which optional columns of the document table fit the width it actually has.
 *
 * The rows are a CSS grid whose columns have fixed floors (DocumentListHeader /
 * DocumentTableRow / FolderTableRow share them), so a table narrower than the
 * sum of those floors scrolls sideways. Horizontal scrollbars are never
 * acceptable, so the table drops optional columns instead — least useful first:
 * Parent and Status, then Date modified (every row's meta line already says
 * when the file last changed), then Tags, then Version. If even the checkbox,
 * name and actions do not fit, the name's floor gives way.
 */
export interface DocumentColumnFlags {
  showCheckbox: boolean;
  showDateCreated: boolean;
  showVersion: boolean;
  showStatus: boolean;
  showTags: boolean;
  showParent: boolean;
  showActions: boolean;
}

type OptionalColumn = "showParent" | "showStatus" | "showDateCreated" | "showTags" | "showVersion";

/** Floors in px, matching the grid templates in the row and header components. */
const FLOOR: Record<keyof DocumentColumnFlags, number> = {
  showCheckbox: 28,
  showDateCreated: 140,
  showVersion: 80,
  showStatus: 100,
  showTags: 180,
  showParent: 150,
  showActions: 50,
};

const DROP_ORDER: OptionalColumn[] = [
  "showParent",
  "showStatus",
  "showDateCreated",
  "showTags",
  "showVersion",
];

/** The rows' `px-6` and `gap-4`, in rem — the app's root size is not always 16px. */
const ROW_PADDING_REM = 3;
const COLUMN_GAP_REM = 1;

export const DEFAULT_NAME_MIN_WIDTH = 240;

/** The narrowest width a row with these columns can take without overflowing. */
export function minimumRowWidth(
  flags: DocumentColumnFlags,
  nameMinWidth: number,
  remPx = 16
): number {
  const shown = (Object.keys(FLOOR) as Array<keyof DocumentColumnFlags>).filter((k) => flags[k]);
  const fixed = shown.reduce((sum, key) => sum + FLOOR[key], 0);
  // The name column is always there, so the columns number shown + 1.
  return (ROW_PADDING_REM + COLUMN_GAP_REM * shown.length) * remPx + nameMinWidth + fixed;
}

export interface FittedColumns {
  flags: DocumentColumnFlags;
  /** Set only when the name's floor itself had to give way. */
  nameMinWidth?: number;
}

/**
 * `available` is the rows' width in px; 0 or less means "not measured yet", and
 * the requested columns are kept as they are. `remPx` is the root font size.
 */
export function fitDocumentColumns(
  requested: DocumentColumnFlags,
  available: number,
  nameMinWidth: number = DEFAULT_NAME_MIN_WIDTH,
  remPx = 16
): FittedColumns {
  if (!(available > 0)) return { flags: requested };
  const flags = { ...requested };
  for (const column of DROP_ORDER) {
    if (minimumRowWidth(flags, nameMinWidth, remPx) <= available) return { flags };
    flags[column] = false;
  }
  const rest = minimumRowWidth(flags, 0, remPx);
  if (rest + nameMinWidth <= available) return { flags };
  return { flags, nameMinWidth: Math.max(0, Math.floor(available - rest)) };
}
